import React, { useEffect, useState } from "react"
import { Routes, Route, Navigate } from "react-router-dom"
import {
  AuthStatus,
  getCurrentStatus,
  subscribeToStatus,
  unsubscribeToStatus,
} from "../auth"

import Header from "../components/Header"

// Pages
import DashboardPage from "./Dashboard"
import LoginPage from "./Login"
import NotFoundPage from "./NotFound"
import AcceptInvitation from "./AcceptInvitation"
import SearchPage from "./SearchPage"

// Admin features
import AdminPage from "./Admin"
import LoginsHistory from "./LoginsHistory"
import MarketInsights from "./MarketInsight"
import FundFactSheet from "./FundFactSheets"
import ShareHoldings from "./Top10ShareHolding"
import SustainabilityScreen from "./Sustainability"

import useFeatureConfig from "../hooks/useFeatureConfig"
import AccountPage from "./Account"
import Suitability from "./Account/Suitability"
import Transactions from "./Account/Transactions"
import PersonPage from "./Person"
import Agreements from "./Account/Agreements"
import Accounts from "./Person/Accounts"
import Documents from "./Person/Documents"
import Communications from "./Person/Communications"
import Details from "./Person/Details"
import Security from "./Person/Security"
import BankAccounts from "./Person/BankAccounts"
import IdVerifications from "./Person/IdVerifications"

const RoutesPath = () => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(
    getCurrentStatus() === "Authenticated"
  )
  const [hasAccessToAdminManagement] = useFeatureConfig(["AdminManagement"])

  useEffect(() => {
    function handleStatusChange(newStatus: AuthStatus) {
      setIsAuthenticated(newStatus === "Authenticated")
    }
    subscribeToStatus(handleStatusChange)
    return () => unsubscribeToStatus(handleStatusChange)
  }, [])

  if (!isAuthenticated) {
    return (
      <Routes>
        <Route path="/accept-invitation" element={<AcceptInvitation />} />
        <Route path="/" element={<LoginPage />} />
      </Routes>
    )
  }

  return (
    <Routes>
      <Route path="/accept-invitation" element={<AcceptInvitation />} />
      <Route
        path="/"
        element={
          <>
            <Header />
            <DashboardPage />
          </>
        }
      />
      <Route
        path="/search"
        element={
          <>
            <Header />
            <SearchPage />
          </>
        }
      />

      {/* AccountPage */}
      <Route
        path="/account/:id/*"
        element={
          <>
            <Header />
            <AccountPage />
          </>
        }
      >
        <Route index element={<Suitability />} />
        <Route path="suitability" element={<Suitability />} />
        <Route path="transactions" element={<Transactions />} />
        <Route path="agreements" element={<Agreements />} />
      </Route>

      {/* PersonPage */}
      <Route
        path="/person/:id/*"
        element={
          <>
            <Header />
            <PersonPage />
          </>
        }
      >
        <Route index element={<Navigate to="accounts" replace />} />
        <Route path="accounts" element={<Accounts />} />
        <Route path="details" element={<Details />} />
        <Route path="documents" element={<Documents />} />
        <Route path="communications" element={<Communications />} />
        <Route path="security" element={<Security />} />
        <Route path="bank-accounts" element={<BankAccounts />} />
        <Route path="id-verifications" element={<IdVerifications />} />
      </Route>

      {/* Admin pages */}
      {hasAccessToAdminManagement && (
        <>
          <Route
            path="/admin"
            element={
              <>
                <Header />
                <AdminPage />
              </>
            }
          />
          <Route
            path="/logins-history"
            element={
              <>
                <Header />
                <LoginsHistory />
              </>
            }
          />
          <Route
            path="/market-insights"
            element={
              <>
                <Header />
                <MarketInsights />
              </>
            }
          />
          <Route
            path="/fund-factsheets"
            element={
              <>
                <Header />
                <FundFactSheet />
              </>
            }
          />
          <Route
            path="/sustainability"
            element={
              <>
                <Header />
                <SustainabilityScreen />
              </>
            }
          />
          <Route
            path="/top10-shareholdings"
            element={
              <>
                <Header />
                <ShareHoldings />
              </>
            }
          />
        </>
      )}

      <Route
        path="*"
        element={
          <>
            <Header />
            <NotFoundPage />
          </>
        }
      />
    </Routes>
  )
}

export default RoutesPath
